import React, { useEffect, useRef, useState } from "react";
import { BrandFrame } from "./shared";

// ─── INSTAGRAM STORIES (9:16 · 1080×1920) ────────────────────────
// Design fisso 540×960 px (rapporto 9:16), poi scale per fit container.
// L'export prende il nodo [data-testid="story-page"] e lo renderizza
// a 1080×1920 via html2canvas.
const STORY_DESIGN_W = 540;
const STORY_DESIGN_H = 960;

function StoryPage({ children, bg, testid = "story-page" }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(() => {
      const w = wrapRef.current?.clientWidth || STORY_DESIGN_W;
      setScale(w / STORY_DESIGN_W);
    });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={wrapRef}
      className="relative rounded-md overflow-hidden mx-auto"
      data-testid={testid}
      style={{
        aspectRatio: `${STORY_DESIGN_W}/${STORY_DESIGN_H}`,
        background: bg,
        maxWidth: 360,
        width: "100%",
        boxShadow: "0 18px 40px -16px rgba(0,0,0,0.5)",
      }}
    >
      <div
        style={{
          width: STORY_DESIGN_W,
          height: STORY_DESIGN_H,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Story 1: "Quanto vale casa tua?" → CTA stima personale dell'agente
export function StoryStima({ data, lay, col }) {
  const isMod = lay.name === "Moderno";
  const isLux = lay.name === "Luxury";
  const isMin = lay.name === "Minimal";
  const isCenter = lay.align === "center";
  const qrUrl = data.estimateQrUrl || data.landingQrUrl;
  return (
    <div data-testid="static-story_stima">
    <StoryPage bg={col.bg} testid="story-page">
      <div className="absolute inset-0 flex flex-col" style={{ color: col.fg, fontFamily: lay.bodyFont, textAlign: isCenter ? "center" : "left" }}>
        <div style={{ height: 8, background: col.accent }} />

        {/* Header — solo overline, logo viene da BrandFrame */}
        <div style={{ padding: "28px 36px 0", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg }}>
            · Stima riservata ·
          </div>
        </div>

        {/* Headline gigante */}
        <div style={{ padding: "60px 36px 0", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.accent, marginBottom: 18 }}>
            {data.areaShort}
          </div>
          <h1
            style={{
              fontFamily: lay.headingFont,
              ...lay.titleStyle,
              fontSize: isMod ? 88 : (isMin ? 78 : 96),
              color: col.fg,
              lineHeight: 0.94,
              letterSpacing: isMod ? "-0.04em" : "-0.025em",
              margin: 0,
            }}
          >
            {isMod ? "QUANTO " : "Quanto "}
            <span style={{ fontStyle: lay.name === "Elegante" || isLux ? "italic" : "normal", color: col.accent }}>
              {isMod ? "VALE" : "vale"}
            </span><br />
            {isMod ? "CASA TUA?" : "casa tua?"}
          </h1>
          {isLux && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: isCenter ? "center" : "flex-start", gap: 14, marginTop: 22 }}>
              <span style={{ flex: isCenter ? 1 : 0, maxWidth: 120, height: 1, background: col.border }} />
              <span style={{ fontSize: 10, letterSpacing: "0.4em", textTransform: "uppercase", color: col.accent, fontWeight: 700 }}>· · ·</span>
              <span style={{ flex: isCenter ? 1 : 0, maxWidth: 120, height: 1, background: col.border }} />
            </div>
          )}
          <p style={{ fontSize: 19, lineHeight: 1.4, color: col.softFg, marginTop: 28, maxWidth: isCenter ? "100%" : 380 }}>
            Una stima professionale, gratuita,<br />
            <strong style={{ color: col.fg }}>in 30 secondi sul telefono.</strong>
          </p>
        </div>

        {/* CTA + QR */}
        <div style={{ padding: "30px 36px 40px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg, marginBottom: 6 }}>
            Apri sul telefono
          </div>
          <div style={{ fontFamily: lay.accentFont, fontStyle: "italic", fontSize: 26, color: col.fg, marginBottom: 18, wordBreak: "break-all" }}>
            {data.estimateUrlShort}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ background: "#fff", padding: 10, borderRadius: 4, border: `1px solid ${col.border}` }}>
              {qrUrl ? (
                <img src={qrUrl} alt="Inquadra" style={{ width: 130, height: 130, display: "block" }} />
              ) : (
                <div style={{ width: 130, height: 130, background: `repeating-linear-gradient(45deg, ${col.fg}, ${col.fg} 4px, #fff 4px, #fff 8px)` }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, lineHeight: 1.35, color: col.softFg }}>
                Inquadra il QR<br />
                oppure tocca il link.<br />
                <strong style={{ color: col.fg }}>{data.agentName}</strong>
              </div>
            </div>
          </div>
        </div>

        <div style={{ height: 8, background: col.accent }} />
        <BrandFrame data={data} lay={lay} theme={col.isDark ? "dark" : "light"} />
      </div>
    </StoryPage>
    </div>
  );
}

// ── Story 2: "Nuovo immobile in vendita" — usa foto property
export function StoryNuovo({ data, lay, col }) {
  const isMod = lay.name === "Moderno";
  const isLux = lay.name === "Luxury";
  const isCenter = lay.align === "center";
  return (
    <div data-testid="static-story_nuovo">
    <StoryPage bg={col.bg} testid="story-page">
      <div className="absolute inset-0 flex flex-col" style={{ color: col.fg, fontFamily: lay.bodyFont }}>
        {/* Foto hero — 60% altezza */}
        <div style={{ position: "relative", height: "58%", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${data.photos.hero})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(to bottom, transparent 60%, ${col.bg} 100%)`,
            }}
          />
          <div style={{ position: "absolute", top: 28, left: 28, padding: "8px 14px", background: col.accent, color: col.isDark ? col.fg : "#fff", fontSize: 11, fontWeight: 800, letterSpacing: "0.32em", textTransform: "uppercase" }}>
            Nuovo · in vendita
          </div>
        </div>

        {/* Testo */}
        <div style={{ flex: 1, padding: "20px 36px 30px", display: "flex", flexDirection: "column", textAlign: isCenter ? "center" : "left" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg, marginBottom: 10 }}>
            {data.city}
          </div>
          <h1
            style={{
              fontFamily: lay.headingFont,
              ...lay.titleStyle,
              fontSize: isMod ? 50 : 56,
              color: col.fg,
              lineHeight: 1.0,
              letterSpacing: "-0.025em",
              margin: 0,
              marginBottom: 16,
            }}
          >
            {data.title}
          </h1>
          {isLux && (
            <div style={{ width: 80, height: 1, background: col.accent, margin: isCenter ? "0 auto 16px" : "0 0 16px" }} />
          )}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: isCenter ? "center" : "flex-start", marginBottom: 22 }}>
            {data.surface ? (
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: col.softFg }}>Superficie</div>
                <div style={{ fontFamily: lay.headingFont, fontSize: 26, color: col.fg, ...lay.titleStyle }}>{data.surface} m²</div>
              </div>
            ) : null}
            {data.rooms ? (
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: col.softFg }}>Locali</div>
                <div style={{ fontFamily: lay.headingFont, fontSize: 26, color: col.fg, ...lay.titleStyle }}>{data.rooms}</div>
              </div>
            ) : null}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: col.softFg }}>Prezzo</div>
              <div style={{ fontFamily: lay.headingFont, fontSize: 26, color: col.accent, ...lay.titleStyle }}>{data.price}</div>
            </div>
          </div>
          <div style={{ marginTop: "auto", paddingTop: 16, borderTop: `1px solid ${col.border}` }} />
        </div>
      </div>
      <BrandFrame data={data} lay={lay} theme={col.isDark ? "dark" : "light"} />
    </StoryPage>
    </div>
  );
}

// ── Story 3: "Venduto" — celebrazione, photo + days-to-sell
export function StoryVenduto({ data, lay, col }) {
  const isMod = lay.name === "Moderno";
  const isLux = lay.name === "Luxury";
  const isCenter = lay.align === "center";
  return (
    <div data-testid="static-story_venduto">
    <StoryPage bg={col.bg} testid="story-page">
      <div className="absolute inset-0 flex flex-col" style={{ color: col.fg, fontFamily: lay.bodyFont }}>
        {/* Foto hero scura */}
        <div style={{ position: "relative", height: "55%", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${data.photos.hero})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.55) saturate(0.85)",
            }}
          />
          {/* Stamp VENDUTO */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-8deg)",
              border: `4px solid ${col.accent}`,
              color: col.accent,
              padding: "16px 36px",
              fontFamily: lay.headingFont,
              fontStyle: lay.name === "Elegante" || isLux ? "italic" : "normal",
              fontSize: 64,
              fontWeight: isMod ? 900 : 400,
              letterSpacing: isMod ? "0.04em" : "-0.01em",
              textTransform: isMod ? "uppercase" : "none",
              background: "rgba(0,0,0,0.30)",
              backdropFilter: "blur(2px)",
            }}
          >
            {isMod ? "VENDUTO" : "Venduto"}
          </div>
        </div>

        {/* Testo */}
        <div style={{ flex: 1, padding: "26px 36px 30px", display: "flex", flexDirection: "column", textAlign: isCenter ? "center" : "left" }}>
          <h2
            style={{
              fontFamily: lay.headingFont,
              ...lay.titleStyle,
              fontSize: isMod ? 38 : 42,
              color: col.fg,
              lineHeight: 1.0,
              letterSpacing: "-0.02em",
              margin: 0,
              marginBottom: 14,
            }}
          >
            {data.title}
          </h2>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg, marginBottom: 22 }}>
            {data.city}
          </div>

          {/* Stat days-to-sell */}
          <div style={{ display: "flex", gap: 28, justifyContent: isCenter ? "center" : "flex-start", marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: col.softFg }}>Venduto in</div>
              <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 56, color: col.accent, lineHeight: 1 }}>
                {data.daysToSell}
                <span style={{ fontSize: 22, color: col.fg, marginLeft: 6 }}>giorni</span>
              </div>
            </div>
          </div>

          <p style={{ fontSize: 16, lineHeight: 1.4, color: col.softFg, marginBottom: 18 }}>
            <strong style={{ color: col.fg }}>{isMod ? "Vendi anche tu" : "Vuoi vendere anche tu?"}</strong> Stima la tua casa in 30 secondi:
            <br />
            <span style={{ fontFamily: lay.accentFont, fontStyle: "italic", color: col.accent, fontSize: 18 }}>{data.estimateUrlShort}</span>
          </p>

          <div style={{ marginTop: "auto", paddingTop: 14, borderTop: `1px solid ${col.border}` }} />
        </div>
      </div>
      <BrandFrame data={data} lay={lay} theme={col.isDark ? "dark" : "light"} />
    </StoryPage>
    </div>
  );
}
